import { Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLang } from "../../contexts/LanguageContext";
import { useLocalized } from "../../lib/localized";
import { imageUrl } from "../../lib/api";

const SECTION_IDS = ["projects", "services", "about", "contact"];

function useActiveSection(enabled) {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setActive(null);
      return;
    }
    const elements = SECTION_IDS
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (elements.length === 0) return;

    const visibility = new Map();
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.intersectionRatio);
        }
        let best = null;
        let bestRatio = 0;
        for (const [id, ratio] of visibility) {
          if (ratio > bestRatio) {
            best = id;
            bestRatio = ratio;
          }
        }
        setActive(bestRatio > 0.15 ? best : null);
      },
      {
        // Account for the fixed 64-80px header; trigger when ~1/3 visible.
        rootMargin: "-80px 0px -40% 0px",
        threshold: [0, 0.15, 0.3, 0.5, 0.75, 1],
      }
    );
    elements.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [enabled]);

  return active;
}

export default function Nav({ content }) {
  const { lang, setLang, t } = useLang();
  const lp = useLocalized();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Scroll-spy only runs on the homepage where the in-page sections live.
  const isHome = location.pathname === "/" || location.pathname === "/en" || location.pathname === "/en/";
  const activeSection = useActiveSection(isHome);

  const isProjectsRoute =
    location.pathname.startsWith("/proyek") || location.pathname.startsWith("/en/projects");

  const links = [
    {
      id: "projects",
      label: content?.nav?.projects,
      href: lp("/proyek", "/en/projects"),
      sectionId: "projects",
    },
    {
      id: "services",
      label: content?.nav?.services,
      href: lp("/#services", "/en#services"),
      sectionId: "services",
    },
    {
      id: "about",
      label: content?.nav?.about,
      href: lp("/#about", "/en#about"),
      sectionId: "about",
    },
    {
      id: "contact",
      label: content?.nav?.contact,
      href: lp("/#contact", "/en#contact"),
      sectionId: "contact",
    },
  ];

  const isLinkActive = (l) => {
    // 'Projects' link is active on /proyek route OR when scrolled to homepage projects section.
    if (l.id === "projects") return isProjectsRoute || (isHome && activeSection === "projects");
    return isHome && activeSection === l.sectionId;
  };

  const wordmark = t(content?.logo?.wordmark) || "Cipta Andalan";
  const logoImg = content?.logo?.image ? imageUrl(content.logo.image) : null;

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#e3e3e1]"
      data-testid="site-nav"
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to={lp("/", "/en")} className="flex items-center gap-3" data-testid="nav-logo">
          {logoImg ? (
            <img
              src={logoImg}
              alt={wordmark}
              className="h-6 md:h-7 w-auto max-w-[120px] object-contain"
            />
          ) : (
            <span className="inline-block w-4 h-4 bg-[#16181c]" aria-hidden />
          )}
          <span className="font-display font-bold text-lg md:text-xl text-[#16181c]">
            {wordmark}
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          {links.map((l) => {
            const active = isLinkActive(l);
            const base =
              "px-4 py-2 rounded-full text-[14px] font-medium transition-colors";
            const cls = active
              ? `${base} bg-[#16181c] text-white`
              : `${base} text-[#16181c] hover:bg-[#f3f3f0]`;
            return l.href.includes("#") ? (
              <a
                key={l.id}
                href={l.href}
                className={cls}
                data-testid={`nav-link-${l.id}`}
                data-active={active || undefined}
                aria-current={active ? "true" : undefined}
              >
                {t(l.label)}
              </a>
            ) : (
              <Link
                key={l.id}
                to={l.href}
                className={cls}
                data-testid={`nav-link-${l.id}`}
                data-active={active || undefined}
                aria-current={active ? "page" : undefined}
              >
                {t(l.label)}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="text-[13px] font-semibold tracking-wide select-none"
            data-testid="lang-toggle"
          >
            <button
              type="button"
              onClick={() => setLang("id")}
              className={lang === "id" ? "text-[#16181c]" : "text-[#5d5f64] hover:text-[#16181c]"}
              data-testid="lang-toggle-id"
              aria-pressed={lang === "id"}
            >
              ID
            </button>
            <span className="mx-2 text-[#5d5f64]">/</span>
            <button
              type="button"
              onClick={() => setLang("en")}
              className={lang === "en" ? "text-[#16181c]" : "text-[#5d5f64] hover:text-[#16181c]"}
              data-testid="lang-toggle-en"
              aria-pressed={lang === "en"}
            >
              EN
            </button>
          </div>
          <button
            type="button"
            className="md:hidden p-2 -mr-2 text-[#16181c]"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-menu-button"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#e3e3e1] bg-white" data-testid="nav-mobile-menu">
          <nav className="container-x py-6 flex flex-col gap-2">
            {links.map((l) => {
              const active = isLinkActive(l);
              const base = "px-4 py-2.5 rounded-full text-base font-medium w-fit transition-colors";
              const cls = active
                ? `${base} bg-[#16181c] text-white`
                : `${base} text-[#16181c] hover:bg-[#f3f3f0]`;
              return l.href.includes("#") ? (
                <a
                  key={l.id}
                  href={l.href}
                  className={cls}
                  data-testid={`nav-link-mobile-${l.id}`}
                  data-active={active || undefined}
                >
                  {t(l.label)}
                </a>
              ) : (
                <Link
                  key={l.id}
                  to={l.href}
                  className={cls}
                  data-testid={`nav-link-mobile-${l.id}`}
                  data-active={active || undefined}
                >
                  {t(l.label)}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
